import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getBankAccounts,
  saveBatchBankAccounts,
} from '../../api/bankAccounts';
import { useBanks } from '../../hooks/useBanks';
import { useFunds } from '../../hooks/useFunds';
import type { BankAccount } from '../../types/BankAccount';
import { t } from '../../i18n/t';

const newBankAccountDefaults = (): Partial<BankAccount> => ({
  acc: '',
  fundId: null,
  bankId: 0,
  branch: null,
});

export function BankAccountsPage() {
  const {
    data: banks,
    loading: banksLoading,
    error: banksError,
  } = useBanks();
  const {
    data: funds,
    loading: fundsLoading,
    error: fundsError,
  } = useFunds();

  const columnDefs = useMemo<ColDef<BankAccount>[]>(() => {
    if (!banks || !funds) return [];

    // Banks dropdown — sorted alphabetically by name.
    const sortedBanks = [...banks].sort((a, b) =>
      a.bankName.localeCompare(b.bankName, 'he'),
    );
    const bankNames = sortedBanks.map((b) => b.bankName);
    const bankNameById = new Map<number, string>(
      banks.map((b) => [b.bankId, b.bankName]),
    );
    const bankIdByName = new Map<string, number>(
      banks.map((b) => [b.bankName, b.bankId]),
    );

    // Funds dropdown — sorted by name; first option is "" representing null.
    const sortedFunds = [...funds].sort((a, b) =>
      a.fundName.localeCompare(b.fundName, 'he'),
    );
    const fundDropdownValues = ['', ...sortedFunds.map((f) => f.fundName)];
    const fundNameById = new Map<number, string>(
      funds.map((f) => [f.fundId, f.fundName]),
    );
    const fundIdByName = new Map<string, number>(
      funds.map((f) => [f.fundName, f.fundId]),
    );

    return [
      {
        field: 'acc',
        headerName: t('bankAccounts.columnAcc'),
        width: 160,
        // PK: editable only for new rows; existing rows keep their acc.
        editable: (params: EditableCallbackParams<BankAccount>) =>
          (params.data as { __status?: string } | undefined)?.__status ===
          'added',
      },
      {
        field: 'bankId',
        headerName: t('bankAccounts.columnBank'),
        flex: 1,
        editable: true,
        // cellDataType: 'text' is REQUIRED on id↔name dropdowns — without it
        // AgGrid infers number from the field type and the dropdown's string
        // output is coerced to NaN before reaching the valueSetter.
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: bankNames },
        valueGetter: (p: ValueGetterParams<BankAccount>) => {
          if (!p.data) return '';
          return bankNameById.get(p.data.bankId) ?? '';
        },
        valueSetter: (p: ValueSetterParams<BankAccount>) => {
          if (!p.data) return false;
          const newId = bankIdByName.get(p.newValue);
          if (newId === undefined) return false;
          p.data.bankId = newId;
          return true;
        },
      },
      {
        field: 'branch',
        headerName: t('bankAccounts.columnBranch'),
        width: 120,
        editable: true,
        cellDataType: 'number',
        valueFormatter: (p: ValueFormatterParams<BankAccount>) =>
          p.value == null ? '' : String(p.value),
      },
      {
        field: 'fundId',
        headerName: t('bankAccounts.columnFund'),
        flex: 1,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: fundDropdownValues },
        valueGetter: (p: ValueGetterParams<BankAccount>) => {
          if (!p.data || p.data.fundId == null) return '';
          return fundNameById.get(p.data.fundId) ?? '';
        },
        valueSetter: (p: ValueSetterParams<BankAccount>) => {
          if (!p.data) return false;
          if (p.newValue === '' || p.newValue == null) {
            p.data.fundId = null;
            return true;
          }
          const newId = fundIdByName.get(p.newValue);
          if (newId === undefined) return false;
          p.data.fundId = newId;
          return true;
        },
      },
    ];
  }, [banks, funds]);

  const refsError = banksError ?? fundsError;
  if (refsError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('bankAccounts.errorPrefix', { message: refsError })}
        </div>
      </div>
    );
  }

  if (banksLoading || fundsLoading || !banks || !funds) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<BankAccount>
      resourceCode="bankAccounts"
      titleKey="bankAccounts.pageTitle"
      idField="acc"
      getRows={getBankAccounts}
      saveBatch={(added, modified, deletedIds) =>
        saveBatchBankAccounts(added, modified, deletedIds as string[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newBankAccountDefaults}
    />
  );
}
